import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  TextField,
  TooltipHost,
  ITooltipHostStyles,
} from "@fluentui/react";
import {
  AddCircleRegular,
  DeleteRegular,
  EditRegular,
  PanelLeftContractRegular,
} from "@fluentui/react-icons";
import { ChatInlineLoader } from "../loading";
import { ChatHistoryListItemCell } from "../ChatHistoryListItemCell/ChatHistoryListItemCell";
import { Modal } from "../modal";
import { useEnterKeyModalDefaultAction } from "../../hooks/useEnterKeyModalDefaultAction";
import type { ConsoleBlade, ConsoleTrailSummary } from "../../api/types";
import type { TokenSource } from "../../api/consoleApi";
import { IngestNowControl } from "../IngestNowControl/IngestNowControl";
import { conversationListId, conversationTitle } from "../../api/conversationIdentity";
import {
  BladeNavIcon,
  knowledgeBaseLabel,
  orderedNavBlades,
} from "./bladeNavIcons";
import styles from "./ChatHistoryPanel.module.css";

const tooltipHostStyles: Partial<ITooltipHostStyles> = { root: { display: "flex" } };

const BLADE_WORK = "work";

/** Matches `.projectsSection` horizontal padding (0.625rem). */
const RAIL_SECTION_PAD_X = 10;

function getProjectMenuPosition(
  triggerRect: DOMRect,
  railRect: DOMRect,
): { top: number; right: number } {
  const top = triggerRect.bottom + 4;
  // Right edge of the menu flush with the rail content edge (inset by section padding).
  const right =
    (typeof window !== "undefined" ? window.innerWidth : railRect.right) -
    railRect.right +
    RAIL_SECTION_PAD_X;
  return { top, right };
}

export type ChatHistoryPanelProps = {
  /**
   * The console rail exactly as the session response gave it (`allowed_blades`).
   * The SPA holds no list of blades and no rule about which are open: the server
   * enables a blade when this release ships a view for it (harness row CC1B-12).
   */
  blades: ConsoleBlade[];
  activeBladeId: string;
  onSelectBlade: (bladeId: string) => void;
  conversations: ConsoleTrailSummary[];
  selectedId: string | null;
  loading: boolean;
  refreshing?: boolean;
  error?: string | null;
  hasMore: boolean;
  searchQuery: string;
  searchOpen: boolean;
  onSearchQueryChange: (value: string) => void;
  onToggleSearch: () => void;
  onNew: () => void;
  onRefresh: () => void;
  onCollapse: () => void;
  onSelect: (trailId: string) => void;
  onRename: (trailId: string, title: string) => void;
  onDelete: (trailId: string) => void | Promise<void>;
  onLoadMore: () => void;
  ingestTokenSource?: TokenSource;
};

export function ChatHistoryPanel({
  blades,
  activeBladeId,
  onSelectBlade,
  conversations,
  selectedId,
  loading,
  refreshing = false,
  error,
  hasMore,
  searchQuery,
  searchOpen: _searchOpen,
  onSearchQueryChange: _onSearchQueryChange,
  onToggleSearch: _onToggleSearch,
  onNew,
  onRefresh,
  onCollapse,
  onSelect,
  onRename,
  onDelete,
  onLoadMore,
  ingestTokenSource,
}: ChatHistoryPanelProps) {
  const navBlades = orderedNavBlades(blades);
  const [knowledgeExpanded, setKnowledgeExpanded] = useState(false);
  const [menuId, setMenuId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const [renameId, setRenameId] = useState<string | null>(null);
  const [renameName, setRenameName] = useState("");
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const menuTargetRef = useRef<HTMLDivElement | null>(null);
  const railRef = useRef<HTMLElement | null>(null);

  const expandKnowledge = React.useCallback(() => {
    setKnowledgeExpanded((expanded) => {
      if (!expanded) {
        onRefresh();
        return true;
      }
      return expanded;
    });
  }, [onRefresh]);

  const toggleKnowledgeExpanded = () => {
    setKnowledgeExpanded((expanded) => {
      const next = !expanded;
      if (next) {
        onRefresh();
      }
      return next;
    });
  };

  useEffect(() => {
    if (activeBladeId === BLADE_WORK) {
      expandKnowledge();
    }
  }, [activeBladeId, expandKnowledge]);

  useEffect(() => {
    const onDown = (event: MouseEvent) => {
      const target = event.target as Element | null;
      if (target?.closest("[data-menu-portal]") || menuTargetRef.current?.contains(event.target as Node)) {
        return;
      }
      setMenuId(null);
    };
    if (menuId) {
      document.addEventListener("mousedown", onDown);
    }
    return () => document.removeEventListener("mousedown", onDown);
  }, [menuId]);

  const filtered = conversations.filter((row) => {
    if (!searchQuery.trim()) {
      return true;
    }
    return conversationTitle(row).toLowerCase().includes(searchQuery.trim().toLowerCase());
  });

  const openMenu = (trailId: string, rect: DOMRect) => {
    const railRect = railRef.current?.getBoundingClientRect();
    if (railRect) {
      setMenuPosition(getProjectMenuPosition(rect, railRect));
    }
    setMenuId((current) => (current === trailId ? null : trailId));
  };

  const handleConfirmDelete = async () => {
    if (!deleteId || deleteLoading) {
      return;
    }
    setDeleteLoading(true);
    try {
      await onDelete(deleteId);
      setDeleteId(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  useEnterKeyModalDefaultAction(Boolean(renameId), () => {
    if (renameId && renameName.trim()) {
      onRename(renameId, renameName.trim());
      setRenameId(null);
      setRenameName("");
    }
  }, { primaryDisabled: !renameName.trim() });
  useEnterKeyModalDefaultAction(Boolean(deleteId), () => {
    void handleConfirmDelete();
  }, { primaryDisabled: deleteLoading });

  const renderKnowledgeChatList = () => {
    if (loading || refreshing) {
      return (
        <div className={styles.knowledgeChatList} data-testid="rail-knowledge-section">
          <div className={styles.loadMoreRow}>
            <ChatInlineLoader srLabel="Loading conversations" />
          </div>
        </div>
      );
    }

    return (
    <div className={styles.knowledgeChatList} data-testid="rail-knowledge-section">
      {error ? (
        <p className={styles.emptyText} data-testid="console-history-refusal">
          {error}
        </p>
      ) : null}
      <div className={styles.projectList}>
        {filtered.map((row, index) => {
          const id = conversationListId(row);
          if (!id) {
            return null;
          }
          return (
            <ChatHistoryListItemCell
              key={id || `row-${index}`}
              trailId={id}
              title={conversationTitle(row)}
              selected={Boolean(selectedId) && selectedId === id}
              menuOpen={menuId === id}
              onSelect={() => onSelect(id)}
              onOpenMenu={(rect) => openMenu(id, rect)}
              menuTargetRef={menuId === id ? menuTargetRef : undefined}
            />
          );
        })}
      </div>
      {hasMore ? (
        <div className={styles.loadMoreFooter}>
          <button
            type="button"
            className={styles.loadMoreButton}
            onClick={onLoadMore}
            data-testid="console-history-load-more"
          >
            Load more...
          </button>
        </div>
      ) : null}
    </div>
    );
  };

  return (
    <section
      ref={railRef}
      className={styles.historyContainer}
      data-is-scrollable
      data-testid="console-history"
    >
      <div className={styles.sidebarHeader}>
        <TooltipHost content="Collapse sidebar" styles={tooltipHostStyles}>
          <button
            type="button"
            className={styles.headerIconButton}
            onClick={onCollapse}
            aria-label="Collapse sidebar"
          >
            <PanelLeftContractRegular className={styles.headerIcon} />
          </button>
        </TooltipHost>
        {ingestTokenSource ? <IngestNowControl getToken={ingestTokenSource} /> : null}
      </div>

      <div className={styles.projectsSection} data-testid="rail-cortex-section">
        {navBlades.map((blade) => {
          if (blade.id === BLADE_WORK) {
            return (
              <React.Fragment key={blade.id}>
                <div className={styles.knowledgeNavRow}>
                  <button
                    type="button"
                    className={`${styles.projectRow} ${
                      activeBladeId === BLADE_WORK ? styles.projectRowActive : ""
                    }`}
                    data-testid="console-blade-work"
                    data-enabled={blade.enabled ? "true" : "false"}
                    aria-expanded={knowledgeExpanded}
                    aria-current={activeBladeId === BLADE_WORK ? "page" : undefined}
                    onClick={toggleKnowledgeExpanded}
                    aria-busy={knowledgeExpanded && (loading || refreshing)}
                  >
                    <BladeNavIcon bladeId={blade.id} className={styles.folderIcon} />
                    <span className={styles.projectName}>
                      {knowledgeBaseLabel(blade.id, blade.label)}
                    </span>
                  </button>
                  <div className={styles.knowledgeActions}>
                    <TooltipHost content="New knowledge chat" styles={tooltipHostStyles}>
                      <button
                        type="button"
                        className={styles.knowledgeActionButton}
                        onClick={(event) => {
                          event.stopPropagation();
                          onNew();
                        }}
                        aria-label="New knowledge chat"
                        data-testid="console-new-conversation"
                      >
                        <AddCircleRegular className={styles.addIcon} />
                      </button>
                    </TooltipHost>
                  </div>
                </div>
                {knowledgeExpanded ? renderKnowledgeChatList() : null}
              </React.Fragment>
            );
          }

          if (blade.enabled) {
            return (
              <button
                key={blade.id}
                type="button"
                className={`${styles.projectRow} ${
                  activeBladeId === blade.id ? styles.projectRowActive : ""
                }`}
                data-testid={`console-blade-${blade.id}`}
                data-enabled="true"
                aria-current={activeBladeId === blade.id ? "page" : undefined}
                onClick={() => onSelectBlade(blade.id)}
              >
                <BladeNavIcon bladeId={blade.id} className={styles.folderIcon} />
                <span className={styles.projectName}>
                  {knowledgeBaseLabel(blade.id, blade.label)}
                </span>
              </button>
            );
          }

          return (
            <div
              key={blade.id}
              className={styles.projectRow}
              data-testid={`console-blade-${blade.id}`}
              data-enabled="false"
              aria-disabled="true"
              style={{ cursor: "default" }}
            >
              <BladeNavIcon bladeId={blade.id} className={styles.folderIcon} />
              <span className={styles.projectName}>
                {knowledgeBaseLabel(blade.id, blade.label)}
              </span>
              {blade.message ? (
                <span className={styles.emptyText}>{blade.message}</span>
              ) : null}
            </div>
          );
        })}
      </div>

      {menuId &&
        createPortal(
          <div
            className={styles.projectMenuPortal}
            data-menu-portal="true"
            style={{ top: menuPosition.top, right: menuPosition.right }}
          >
            <button
              type="button"
              className={styles.projectMenuItem}
              onClick={() => {
                const row = conversations.find((item) => item.trail_id === menuId);
                setRenameId(menuId);
                setRenameName(row ? conversationTitle(row) : "");
                setMenuId(null);
              }}
            >
              <EditRegular />
              Rename
            </button>
            <button
              type="button"
              className={`${styles.projectMenuItem} ${styles.projectMenuItemDanger}`}
              onClick={() => {
                setDeleteId(menuId);
                setMenuId(null);
              }}
            >
              <DeleteRegular />
              Delete
            </button>
          </div>,
          document.body,
        )}

      <Modal
        isOpen={Boolean(renameId)}
        onClose={() => {
          setRenameId(null);
          setRenameName("");
        }}
        title="Rename"
        primaryAction={{
          label: "Save",
          onClick: () => {
            if (renameId && renameName.trim()) {
              onRename(renameId, renameName.trim());
            }
            setRenameId(null);
            setRenameName("");
          },
          disabled: !renameName.trim(),
        }}
        secondaryAction={{
          label: "Cancel",
          onClick: () => {
            setRenameId(null);
            setRenameName("");
          },
        }}
      >
        <TextField
          label="Project name"
          value={renameName}
          onChange={(_, value) => setRenameName(value ?? "")}
          autoFocus
        />
      </Modal>

      <Modal
        isOpen={Boolean(deleteId)}
        onClose={() => {
          if (!deleteLoading) {
            setDeleteId(null);
          }
        }}
        title="Delete this conversation?"
        primaryAction={{
          label: "Delete",
          onClick: () => void handleConfirmDelete(),
          disabled: deleteLoading,
          pending: deleteLoading,
          pendingText: "Deleting…",
        }}
        secondaryAction={{
          label: "Cancel",
          onClick: () => setDeleteId(null),
          disabled: deleteLoading,
        }}
      >
        <p>It stays in Cortex&apos;s records but leaves your list.</p>
      </Modal>
    </section>
  );
}

export { ChatHistoryPanel as KnowledgeRail };
export const KNOWLEDGE_PAGE_SIZE = 10;
